/**
 * TranslatedAnswer (#2109 R2-B2, R2-B5, R2-B14; Victor 2026-11-02).
 *
 * Staff read a family's free-text answer in English, with the family's own
 * words one tap away. Victor 2026-11-02: "The TRFers should be able to view
 * the full application in english, with an option to view the original." This
 * replaces the 2026-09-30 R1 layout, which printed both versions together.
 *
 *   My daughter has leukemia…
 *   English — machine translation · [View original]
 *   (open) Spanish (original)
 *          Mi hija tiene leucemia…
 *
 * The English is shown ONLY when the stored translation is well formed and was
 * made from the answer as it stands now (`source_hash` equals sha256 of the
 * original). When the family answered in another language and there is no such
 * English — the model failed, translation is off, it has not run yet, or staff
 * edited the answer since (R2-B14) — the original shows with "Not translated
 * yet". An output identical to the source (R2-B5 `same_as_original`), a name,
 * an identifier and an English answer show the original alone.
 *
 * The application's language stamp is the SCREEN language, not the language
 * the family wrote in (a family on the English screen can write in Spanish),
 * so an `en` stamp names the original "Original", never "English (original)".
 *
 * Staff pages are English-only (Victor 2026-09-30), so the copy is English
 * literal.
 */
import { useId, useState, type ReactNode } from 'react';
import { bcp47For } from '@/i18n/negotiate';
import { languageDisplayName } from '@/config/languages';
import type { AnswerTranslation } from '@/types/applications';
import { isSameAsOriginal, looksNonEnglish, usableTranslation } from './translatedAnswerLookup';

export interface TranslatedAnswerRenderArgs {
  /** The family's original words. */
  text: string;
  /** BCP 47 tag for the original; undefined when it is English or unknown. */
  lang: string | undefined;
}

export interface TranslatedAnswerProps {
  /** The family's answer as stored now. */
  original: string;
  /** The English translation, `answer_translations[section][field].en`. */
  translation?: AnswerTranslation | null;
  /** The language the family answered in. Absent on anonymous poll rows. */
  language?: string | null;
  /** A person's name: marked `translate="no"`, never replaced by a translation. */
  name?: boolean;
  /** False for an identifier (an address, a phone): the original alone, no note. */
  translatable?: boolean;
  /**
   * "Translate to English" (Victor 2026-11-02): asks the server to translate
   * this record's answers that have no English, and resolves once the page has
   * them. Offered beside "Not translated yet" only. Absent on poll and send
   * rows, which have their own translation path.
   */
  onTranslate?: () => Promise<unknown>;
  /** Places the ORIGINAL (e.g. inside an edit button — the original is what is
   *  edited). The English and the labels stay outside it. */
  children?: (args: TranslatedAnswerRenderArgs) => ReactNode;
  className?: string;
}

function langOf(language: string | null | undefined): string | undefined {
  if (!language || language === 'en') return undefined;
  return bcp47For(language);
}

const NOTE_CLASS = 'block text-meta text-stevens-ink-3';

export function TranslatedAnswer({
  original,
  translation,
  language,
  name = false,
  translatable = true,
  onTranslate,
  children,
  className,
}: TranslatedAnswerProps) {
  const originalLang = langOf(language);
  const translates = translatable && !name;
  const english = translates ? usableTranslation(original, translation) : null;

  const body = children ? (
    children({ text: original, lang: originalLang })
  ) : (
    <span lang={originalLang} translate={name ? 'no' : undefined}>
      {original}
    </span>
  );

  if (english !== null) {
    return (
      <EnglishFirst
        english={english}
        originalLabel={originalLabelFor(language)}
        className={className}
      >
        {body}
      </EnglishFirst>
    );
  }

  // The family wrote in another language and no current English exists. The
  // stamp is only the screen language, so an English-stamped answer that looks
  // non-English counts too (Victor 2026-11-02).
  const awaiting = translates
    && original.trim() !== ''
    && !isSameAsOriginal(original, translation)
    && (Boolean(originalLang) || looksNonEnglish(original));
  if (awaiting) {
    return (
      <span className={className ? `block ${className}` : 'block'}>
        <span className="block">{body}</span>
        <NotTranslatedYet onTranslate={onTranslate} />
      </span>
    );
  }

  return <span className={className}>{body}</span>;
}

function NotTranslatedYet({ onTranslate }: { onTranslate?: () => Promise<unknown> }) {
  const [state, setState] = useState<'idle' | 'busy' | 'failed'>('idle');
  if (!onTranslate) return <span className={`${NOTE_CLASS} mt-1`}>Not translated yet</span>;
  const run = async () => {
    setState('busy');
    try {
      await onTranslate();
      setState('idle');
    } catch {
      setState('failed');
    }
  };
  return (
    <span className={`${NOTE_CLASS} mt-1`}>
      {state === 'failed' ? 'Could not translate. ' : 'Not translated yet · '}
      <button
        type="button"
        className="inline-flex min-h-[44px] items-center underline underline-offset-2 hover:text-foreground disabled:opacity-60 sm:min-h-0"
        disabled={state === 'busy'}
        onClick={run}
      >
        {state === 'busy' ? 'Translating…' : state === 'failed' ? 'Try again' : 'Translate to English'}
      </button>
    </span>
  );
}

/** An `en` stamp is the screen language, not the writing language (see header). */
function originalLabelFor(language: string | null | undefined): string {
  const name = language && language !== 'en' ? languageDisplayName(language) : '';
  return name ? `${name} (original)` : 'Original';
}

function EnglishFirst({
  english,
  originalLabel,
  className,
  children,
}: {
  english: string;
  originalLabel: string;
  className?: string;
  children: ReactNode;
}) {
  const [showOriginal, setShowOriginal] = useState(false);
  const originalId = useId();
  return (
    <span className={className ? `block ${className}` : 'block'} data-testid="translated-answer">
      <span className="block" lang="en">{english}</span>
      <span className={`${NOTE_CLASS} mt-1`}>
        English — machine translation ·{' '}
        <button
          type="button"
          className="inline-flex min-h-[44px] items-center underline underline-offset-2 hover:text-foreground sm:min-h-0"
          aria-expanded={showOriginal}
          aria-controls={originalId}
          onClick={() => setShowOriginal((v) => !v)}
        >
          {showOriginal ? 'Hide original' : 'View original'}
        </button>
      </span>
      {showOriginal && (
        <span id={originalId} className="mt-2 block border-l-2 border-stevens-ink-3/30 pl-3">
          <span className={NOTE_CLASS}>{originalLabel}</span>
          <span className="block">{children}</span>
        </span>
      )}
    </span>
  );
}
